import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Dashboard from 'services/Dashboard'
import PieChart from 'components/Charts/PieChart'
import ColumnChart from 'components/Charts/ColumnChart'
import HistoryDialog from 'components/Charts/HistoryDialog'
import Select from 'components/Inputs/Select'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  InfoContainer,
  FilterContainer,
  LoadingContainer,
  SubTitle,
  InfoText,
  LinkText
} from '../../styles'

export default function MembersByAgeRange ({
  data,
  month = DateTime.now ().month,
  year = DateTime.now ().year
}) {
  const selectedDate = DateTime.local (year, month).setLocale ('pt-BR')
  const formattedDate = selectedDate.toFormat ('MMM yyyy')
  const token = AuthService.getToken ()
  const periodFilterOptions = Dashboard.PERIOD_FILTERS
  const [loading, setLoading] = useState (true)
  const [isMounted, setMounted] = useState (false)
  const [periodFilter, setPeriodFilter] = useState (null)
  const [showDetails, setShowDetails] = useState (false)
  const [membersByAgeRange, setMembersByAgeRange] = useState (null)

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (isMounted) setPeriodFilter (periodFilterOptions[0])
  }, [isMounted])

  useEffect (() => {
    getData ()
  }, [periodFilter])

  const getData = async () => {
    if (showDetails) try {
      setLoading (true)

      const startDate = selectedDate.minus ({ months: periodFilter.value }).startOf ('month').toISODate ()
      const endDate = selectedDate.endOf ('month').toISODate ()

      const filters = `start_date=${startDate}&end_date=${endDate}`

      const { data: membersByAgeRange } = await cutileiApi.get (
        `/admin/dashboard/members_by_age_range?${filters}`, requestConfig
      )

      setMembersByAgeRange ([
        ['Faixa etária', 'Menos de 18', '18 a 24', '25 a 34', '35 a 44', '45 a 54', '55 a 64', '65 ou mais'],
        ...Object.keys (membersByAgeRange).map (month => ([
          month,
          membersByAgeRange[month]['Menos de 18'] ?? 0,
          membersByAgeRange[month]['18 a 24'] ?? 0,
          membersByAgeRange[month]['25 a 34'] ?? 0,
          membersByAgeRange[month]['35 a 44'] ?? 0,
          membersByAgeRange[month]['45 a 54'] ?? 0,
          membersByAgeRange[month]['55 a 64'] ?? 0,
          membersByAgeRange[month]['65 ou mais'] ?? 0
        ]))
      ])
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const toggleDetails = () => {
    if (!isMounted) setMounted (true)
    setShowDetails (!showDetails)
  }

  if (!data || Object.keys (data).length === 0) {
    return null
  }

  const monthMembersByAgeRange = [
    ['Faixa etária (anos)', 'Membros'],
    ['Menos de 18', data[formattedDate]?.['Menos de 18'] ?? 0],
    ['18 a 24', data[formattedDate]?.['18 a 24'] ?? 0],
    ['25 a 34', data[formattedDate]?.['25 a 34'] ?? 0],
    ['35 a 44', data[formattedDate]?.['35 a 44'] ?? 0],
    ['45 a 54', data[formattedDate]?.['45 a 54'] ?? 0],
    ['55 a 64', data[formattedDate]?.['55 a 64'] ?? 0],
    ['65 ou mais', data[formattedDate]?.['65 ou mais'] ?? 0]
  ]

  return (
    <InfoContainer>
      <SubTitle>Membros por faixa etária - {selectedDate.toFormat ('MMMM / yyyy')}</SubTitle>
      <PieChart
        data={monthMembersByAgeRange}
        format='amount'
      />
      <LinkText onClick={toggleDetails}>
        Ver histórico
      </LinkText>
      <HistoryDialog
        visible={showDetails}
        title='Membros por faixa etária'
        onClose={toggleDetails}
      >
        <FilterContainer>
          <Select
            name='periodFilter'
            placeholder='Período...'
            value={periodFilter}
            options={periodFilterOptions}
            onChange={(_, option) => setPeriodFilter (option)}
            containerStyles={{width: 250}}
          />
        </FilterContainer>
        {loading ? (
          <LoadingContainer>
            <Loading/>
            <InfoText>Gerando gráfico...</InfoText>
          </LoadingContainer>
        ) : (
          <ColumnChart
            data={membersByAgeRange}
            options={{
              isStacked: true,
              chartArea: {
                top: 35, left: 110, width: '100%', height: '80%'
              },
              vAxis: {
                title: 'Membros por faixa etária',
                titleTextStyle: {
                  bold: true
                }
              }
            }}
          />
        )}
      </HistoryDialog>
    </InfoContainer>
  )
}
